import { z } from 'zod'

/**
 * O horário de trabalho de cada pessoa (10/10/2026).
 *
 * Substitui o horário da empresa, que ficava em Configuração > Regras do
 * sistema. Pedido do Mike: "cada usuário deve ser configurado dia e horário de
 * trabalho, só pode acessar dentro do horário, se estiver fora é desconectado
 * e só consegue conectar novamente no horário", com quatro caixas por dia
 * para o almoço ("08:00 até 12:00, 13:00 até 18:00").
 *
 * O almoço não mexe no status: ele descartou a pausa automática, porque
 * jogar o vendedor para Pausado tiraria dele o que é dele. O acesso vale da
 * primeira entrada à última saída; o intervalo só conta para o aviso de "fora
 * do seu horário" ao agendar.
 *
 * Mora no pacote compartilhado porque as mesmas contas servem ao login, à
 * sessão, ao worker e à tela, que mostra a contagem e o aviso amarelo. Nada
 * aqui usa banco nem Node.
 */

/**
 * Os dias da semana como a tabela os guarda.
 *
 * A chave é a abreviação que Intl.DateTimeFormat('pt-BR') devolve, **com o
 * acento de "sáb"**. Gravar "sab" faz o sábado nunca ser encontrado na tabela,
 * e um sábado configurado como dia de trabalho se comporta como fechado sem
 * nada na tela indicando o porquê.
 */
export const DIAS_DA_SEMANA: { chave: string; nome: string }[] = [
  { chave: 'seg', nome: 'Segunda' },
  { chave: 'ter', nome: 'Terça' },
  { chave: 'qua', nome: 'Quarta' },
  { chave: 'qui', nome: 'Quinta' },
  { chave: 'sex', nome: 'Sexta' },
  { chave: 'sáb', nome: 'Sábado' },
  { chave: 'dom', nome: 'Domingo' },
]

/** Uma faixa (entrada e saída) ou duas (com o almoço no meio), em "HH:MM". */
export type HorasDoDia = [string, string] | [string, string, string, string]

/** Dia sem horas é dia de folga. */
export type HorarioDeTrabalho = Record<string, HorasDoDia | null>

/** Depois da saída, o aviso conta dez minutos até desconectar. */
export const MINUTOS_ATE_DESCONECTAR = 10
/** "Mais 30 minutos", uma vez por dia. */
export const MINUTOS_DA_PRORROGACAO = 30

const hora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'hora inválida')

export const horarioDeTrabalhoSchema = z
  .record(
    z.string(),
    z.union([z.tuple([hora, hora]), z.tuple([hora, hora, hora, hora])]).nullable(),
  )
  .superRefine((tabela, contexto) => {
    for (const dia of DIAS_DA_SEMANA) {
      const problema = problemaDoDia(horasDoDia(tabela, dia.chave))
      if (problema) contexto.addIssue({ code: 'custom', message: `${dia.nome}: ${problema}` })
    }
  })

export const definirHorarioDeTrabalhoSchema = z.object({ horario: horarioDeTrabalhoSchema })

/** As horas de um dia. Aceita o "sab" antigo ao ler, para o que já está gravado continuar valendo. */
export function horasDoDia(
  tabela: Record<string, HorasDoDia | null> | null | undefined,
  chave: string,
): HorasDoDia | null {
  if (!tabela) return null
  const direta = tabela[chave]
  if (direta !== undefined) return direta
  if (chave === 'sáb') return tabela.sab ?? null
  return null
}

/** O que está errado nas horas de um dia, ou null quando dá para gravar. */
export function problemaDoDia(horas: HorasDoDia | null): string | null {
  if (!horas) return null
  const [entrada, saidaAlmoco, volta, saida] = horas
  if (saidaAlmoco <= entrada) {
    return horas.length === 2
      ? 'a saída precisa ser depois da entrada'
      : 'a saída para o almoço precisa ser depois da entrada'
  }
  if (volta !== undefined && saida !== undefined) {
    if (volta <= saidaAlmoco) return 'a volta do almoço precisa ser depois da saída para o almoço'
    if (saida <= volta) return 'a saída precisa ser depois da volta do almoço'
  }
  return null
}

/** Quantos dias da semana têm horário. */
export function diasDeTrabalho(tabela: HorarioDeTrabalho | null | undefined): number {
  return DIAS_DA_SEMANA.filter((d) => horasDoDia(tabela, d.chave)).length
}

function minutos(hhmm: string): number {
  return Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))
}

/** As faixas do dia em minutos desde a meia-noite. */
function faixas(horas: HorasDoDia): [number, number][] {
  return horas.length === 2
    ? [[minutos(horas[0]), minutos(horas[1])]]
    : [
        [minutos(horas[0]), minutos(horas[1])],
        [minutos(horas[2]), minutos(horas[3])],
      ]
}

// ── O relógio do fuso ──
//
// O servidor roda num contêiner em UTC e a tela, num navegador que pode estar
// em qualquer fuso: "8h" precisa ser 8h no fuso da empresa nos dois.

/** Montar um `Intl.DateTimeFormat` custa caro; um por fuso basta. */
const formatadores = new Map<string, Intl.DateTimeFormat>()

interface NoFuso {
  ano: number
  mes: number
  dia: number
  /** A chave do dia da semana em DIAS_DA_SEMANA. */
  chave: string
  /** Minutos desde a meia-noite. */
  minutos: number
  /** O mesmo relógio lido como se fosse UTC, para medir o deslocamento. */
  comoUtc: number
}

function noFuso(instante: Date, fuso: string): NoFuso {
  let formatador = formatadores.get(fuso)
  if (!formatador) {
    formatador = new Intl.DateTimeFormat('pt-BR', {
      timeZone: fuso,
      hourCycle: 'h23',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    formatadores.set(fuso, formatador)
  }
  const partes = formatador.formatToParts(instante)
  const valor = (tipo: Intl.DateTimeFormatPartTypes) =>
    partes.find((p) => p.type === tipo)?.value ?? ''
  const ano = Number(valor('year'))
  const mes = Number(valor('month'))
  const dia = Number(valor('day'))
  const h = Number(valor('hour'))
  const m = Number(valor('minute'))
  const s = Number(valor('second'))
  return {
    ano,
    mes,
    dia,
    // "sáb." vira "sáb", a chave da tabela.
    chave: valor('weekday').replace('.', '').slice(0, 3).toLowerCase(),
    minutos: h * 60 + m,
    comoUtc: Date.UTC(ano, mes - 1, dia, h, m, s),
  }
}

/** O instante em que o relógio do fuso marca aquele dia e aqueles minutos. */
function instanteNoFuso(ano: number, mes: number, dia: number, min: number, fuso: string): Date {
  const ingenuo = Date.UTC(ano, mes - 1, dia, Math.floor(min / 60), min % 60)
  const deslocamento = (t: number) => {
    const d = new Date(t)
    return noFuso(d, fuso).comoUtc - Math.floor(t / 1000) * 1000
  }
  // Duas passadas: a segunda corrige quando a primeira caiu do outro lado de
  // uma troca de horário de verão.
  const primeira = ingenuo - deslocamento(ingenuo)
  return new Date(ingenuo - deslocamento(primeira))
}

/** O dia seguinte do calendário, sem depender do fuso de quem roda o código. */
function diaSeguinte(ano: number, mes: number, dia: number, n: number) {
  const d = new Date(Date.UTC(ano, mes - 1, dia + n))
  return { ano: d.getUTCFullYear(), mes: d.getUTCMonth() + 1, dia: d.getUTCDate() }
}

function chaveDoDia(ano: number, mes: number, dia: number, fuso: string): string {
  // Meio-dia: longe de qualquer virada de dia por causa do fuso.
  return noFuso(instanteNoFuso(ano, mes, dia, 12 * 60, fuso), fuso).chave
}

/** Se dois instantes caem no mesmo dia do calendário, no fuso. */
export function mesmoDiaNoFuso(a: Date, b: Date, fuso: string): boolean {
  const x = noFuso(a, fuso)
  const y = noFuso(b, fuso)
  return x.ano === y.ano && x.mes === y.mes && x.dia === y.dia
}

export interface SituacaoNoHorario {
  /** Tem pelo menos um dia de trabalho na semana. */
  temHorario: boolean
  /** O login aceita agora: entre a primeira entrada e a última saída de hoje. */
  podeEntrar: boolean
  /** A sessão continua de pé agora: até a desconexão de hoje. */
  podeFicar: boolean
  /** A última saída de hoje, quando hoje é dia de trabalho. */
  saida: Date | null
  /** Quando a sessão cai: dez minutos depois da saída, ou quarenta com a prorrogação. */
  desconexao: Date | null
  /** A próxima entrada que ainda não passou, para o login dizer quando voltar. */
  proximaEntrada: Date | null
}

/**
 * Onde a pessoa está em relação ao próprio horário.
 *
 * `prorrogadoEm` é quando ela usou o "Mais 30 minutos"; só conta se foi hoje.
 */
export function situacaoNoHorario(
  tabela: HorarioDeTrabalho | null | undefined,
  agora: Date,
  fuso: string,
  prorrogadoEm: Date | null = null,
): SituacaoNoHorario {
  const hoje = noFuso(agora, fuso)
  const horasDeHoje = horasDoDia(tabela, hoje.chave)
  const temHorario = diasDeTrabalho(tabela) > 0

  let saida: Date | null = null
  let desconexao: Date | null = null
  let podeEntrar = false
  let podeFicar = false
  let proximaEntrada: Date | null = null

  if (horasDeHoje) {
    const lista = faixas(horasDeHoje)
    const entrada = lista[0][0]
    const fim = lista[lista.length - 1][1]
    const prorrogado = prorrogadoEm !== null && mesmoDiaNoFuso(prorrogadoEm, agora, fuso)
    saida = instanteNoFuso(hoje.ano, hoje.mes, hoje.dia, fim, fuso)
    desconexao = new Date(
      saida.getTime() +
        (MINUTOS_ATE_DESCONECTAR + (prorrogado ? MINUTOS_DA_PRORROGACAO : 0)) * 60_000,
    )
    podeEntrar = hoje.minutos >= entrada && agora < saida
    podeFicar = hoje.minutos >= entrada && agora < desconexao
    if (hoje.minutos < entrada) {
      proximaEntrada = instanteNoFuso(hoje.ano, hoje.mes, hoje.dia, entrada, fuso)
    }
  }

  // A próxima entrada fora de hoje: o primeiro dia de trabalho da semana que vem.
  for (let n = 1; !proximaEntrada && temHorario && n <= 7; n++) {
    const d = diaSeguinte(hoje.ano, hoje.mes, hoje.dia, n)
    const horas = horasDoDia(tabela, chaveDoDia(d.ano, d.mes, d.dia, fuso))
    if (horas) proximaEntrada = instanteNoFuso(d.ano, d.mes, d.dia, faixas(horas)[0][0], fuso)
  }

  return { temHorario, podeEntrar, podeFicar, saida, desconexao, proximaEntrada }
}

/**
 * Se um instante cai dentro das faixas de trabalho, almoço de fora. É o que
 * decide o aviso amarelo ao agendar mensagem ou retorno.
 */
export function dentroDoHorario(
  tabela: HorarioDeTrabalho | null | undefined,
  instante: Date,
  fuso: string,
): boolean {
  const momento = noFuso(instante, fuso)
  const horas = horasDoDia(tabela, momento.chave)
  if (!horas) return false
  return faixas(horas).some(([de, ate]) => momento.minutos >= de && momento.minutos < ate)
}

/** "hoje às 08:00", "amanhã às 08:00" ou "segunda, 12/10, às 08:00". */
export function quandoVolta(entrada: Date, agora: Date, fuso: string): string {
  const e = noFuso(entrada, fuso)
  const hhmm = `${String(Math.floor(e.minutos / 60)).padStart(2, '0')}:${String(e.minutos % 60).padStart(2, '0')}`
  if (mesmoDiaNoFuso(entrada, agora, fuso)) return `hoje às ${hhmm}`
  const a = noFuso(agora, fuso)
  const amanha = diaSeguinte(a.ano, a.mes, a.dia, 1)
  if (amanha.ano === e.ano && amanha.mes === e.mes && amanha.dia === e.dia) {
    return `amanhã às ${hhmm}`
  }
  const nome = DIAS_DA_SEMANA.find((d) => d.chave === e.chave)?.nome.toLowerCase() ?? ''
  const data = `${String(e.dia).padStart(2, '0')}/${String(e.mes).padStart(2, '0')}`
  return `${nome}, ${data}, às ${hhmm}`
}

/**
 * O resumo da semana numa linha, para a lista de usuários:
 * "seg a sex, 08:00 às 12:00 e 13:00 às 18:00".
 */
export function resumoDoHorario(tabela: HorarioDeTrabalho | null | undefined): string {
  const linhas: { dias: string[]; texto: string }[] = []
  for (const dia of DIAS_DA_SEMANA) {
    const horas = horasDoDia(tabela, dia.chave)
    if (!horas) continue
    const texto =
      horas.length === 2
        ? `${horas[0]} às ${horas[1]}`
        : `${horas[0]} às ${horas[1]} e ${horas[2]} às ${horas[3]}`
    const ultima = linhas[linhas.length - 1]
    const anterior = DIAS_DA_SEMANA[DIAS_DA_SEMANA.indexOf(dia) - 1]
    if (ultima && ultima.texto === texto && anterior && ultima.dias.at(-1) === anterior.chave) {
      ultima.dias.push(dia.chave)
    } else {
      linhas.push({ dias: [dia.chave], texto })
    }
  }
  if (linhas.length === 0) return 'sem horário'
  return linhas
    .map((l) => {
      const dias =
        l.dias.length === 1
          ? l.dias[0]
          : l.dias.length === 2
            ? `${l.dias[0]} e ${l.dias[1]}`
            : `${l.dias[0]} a ${l.dias[l.dias.length - 1]}`
      return `${dias}, ${l.texto}`
    })
    .join('; ')
}
